import { endOfDay, format, intervalToDuration, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export const DateFormatter = {
  ShortYearDate: 'yy.MM.dd',
  ShortYearDateTime: 'yy.MM.dd HH:mm',
  ShortYearDateTimeSec: 'yy.MM.dd HH:mm:ss',
  Date: 'yyyy.MM.dd',
  DateTime: 'yyyy.MM.dd HH:mm',
  DateTimeSec: 'yyyy.MM.dd HH:mm:ss',
  Time: 'HH:mm',
  TimeSec: 'HH:mm:ss',
  Month: 'yyyy.MM',
  MonthDateTime: 'MM.dd HH:mm',
  EnMonthDate: 'MMM dd, yyyy',
  EnMonthShortDate: 'MMM d, yyyy',
  EnMonthDateTime: 'MMM dd, yyyy, HH:mm',
  EnMonthAtDateTime: "MMM dd, yyyy 'at' HH:mm",
  DateTimeSlash: 'yyyy/MM/dd HH:mm',
} as const;

export function convertDate(date: Date | string | number | null | undefined): Date | null {
  if (!date || date === '0') return null;
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date / 1000000);
  if (date[date.length - 1] === 'Z') return new Date(date);
  return new Date(Number(date.substring(0, 13)));
}

/**
 * @deprecated
 * use formatDatetime
 */
export const formatUTCTimeZone = (date: Date | string | undefined, formatStr?: string) => {
  const time = convertDate(date);
  if (!time) return '';
  return formatInTimeZone(time, 'Etc/UTC', formatStr ?? DateFormatter.DateTime);
};

interface DateFormatterOptions {
  format?: string;
  isUTC?: boolean;
}

export const formatDatetime = (
  value: Date | string | number | null | undefined,
  { format: formatStr = DateFormatter.DateTime, isUTC }: DateFormatterOptions = {},
) => {
  const date = convertDate(value);
  if (!date) return '';
  if (isUTC) return formatInTimeZone(date, 'Etc/UTC', formatStr);
  return format(date, formatStr);
};

export const getRangeStatus = (start?: string, end?: string) => {
  const now = Date.now();
  const isAfterStart = !!start && now > convertDate(start)!.getTime();
  const isAfterEnd = !!end && now > convertDate(end)!.getTime();

  return {
    isBefore: !isAfterStart,
    isStart: isAfterStart,
    isBetween: isAfterStart && !isAfterEnd,
    isEnd: isAfterEnd,
  };
};

export function dateToEpochString(date: Date) {
  return `${date.getTime()}${'0'.repeat(6)}`;
}

export const getTimezoneOffset = () => {
  const offset = new Date().getTimezoneOffset() / -60;
  return `UTC${offset >= 0 ? '+' : ''}${offset}`;
};

export const getStartOfToday = (isUTC: boolean = false) => {
  const now = new Date();
  if (!isUTC) return startOfDay(now);
  return new Date(now.setUTCHours(0, 0, 0, 0));
};

export const getEndOfToday = (isUTC: boolean = false) => {
  const now = new Date();
  if (!isUTC) return endOfDay(now);
  return new Date(now.setUTCHours(23, 59, 59, 999));
};

export const isSameUTCYear = (a: string, b: string) => {
  return convertDate(a)?.getUTCFullYear() === convertDate(b)?.getUTCFullYear();
};

const IntervalMapper = {
  dd: 'days',
  hh: 'hours',
  mm: 'minutes',
  ss: 'seconds',
} as const;

export const formatDistance = (start: Date, end: Date, formatStr: string) => {
  const duration = intervalToDuration({ start, end });

  return Object.entries(IntervalMapper).reduce<string>((acc, [regex, unit]) => {
    const unitValue = duration[unit] ?? 0;
    return acc.replace(regex, unitValue.toString().padStart(2, '0'));
  }, formatStr);
};

export const timestampToDate = (timestamp: string): Date => {
  return new Date(Number(timestamp.slice(0, -6)));
};

// UTC 기준으로 날짜만 보존하는 헬퍼
export const startOfUTCDay = (date: Date): Date => {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
};

export const addDaysUTC = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};
